/**
 * Stakeholder Dashboard - View standup notes, hours, costs, and progress
 */

import React from 'react';
import { useNavigate } from 'react-router';
import { Task, Priority } from '../types';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Calendar, Clock, DollarSign, TrendingUp, AlertCircle } from 'lucide-react';
import MilestoneTracker from '../components/MilestoneTracker';
import { useApp } from '../providers/AppProvider';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { canViewProjectFinance } from '../utils/projectAccess';
import { ensureCostItemCode } from '../utils/costItems';
import { parseDateLike } from '../utils/dateUtils';

export default function StakeholderDashboard() {
  const navigate = useNavigate();
  const { state, loading, currentDate } = useApp();

  if (loading || !state) {
    return <div className="p-8 text-center">Loading stakeholder dashboard...</div>;
  }

  if (!state.currentUser) {
    return <div className="p-8 text-center">User context not loaded</div>;
  }

  const { projects: orgProjects, tasks: orgTasks, users: orgUsers } = useOrganizationData(state) as {
    projects: any[];
    tasks: Task[];
    users: any[];
  };
  
  // Default cost per hour from project (can be customized)
  const defaultCostPerHour = 75; // $75/hour
  
  // Date range filter
  const [dateRange, setDateRange] = React.useState<'today' | 'week' | 'month' | 'custom'>('week');
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  
  const primaryProject = orgProjects[0] || null;
  const allTasks = orgTasks;
  const financeAccessibleProjectIds = new Set(
    orgProjects
      .filter((project) => canViewProjectFinance(state.currentUser, project))
      .map((project) => project.id)
  );
  const canViewAnyFinance = financeAccessibleProjectIds.size > 0;
  const financeTasks = allTasks.filter((task) => financeAccessibleProjectIds.has(task.projectId));
  
  // Filter tasks based on date range
  const getDateFilter = () => {
    const now = new Date();
    let start: Date;
    let end = new Date(now);
    
    switch (dateRange) {
      case 'today':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'week':
        start = new Date(now);
        start.setDate(start.getDate() - 7);
        break;
      case 'month':
        start = new Date(now);
        start.setMonth(start.getMonth() - 1);
        break;
      case 'custom':
        start = parseDateLike(startDate) || new Date(0);
        end = parseDateLike(endDate) || new Date(now);
        break;
      default:
        start = new Date(0);
    }
    
    return { start, end };
  };
  
  const { start: filterStart, end: filterEnd } = getDateFilter();
  
  // Filter standup notes by date range
  const standupNotesInRange = allTasks.flatMap(task => 
    (task.standupNotes || [])
      .filter(note => {
        const noteDate = parseDateLike((note as any).createdAt || (note as any).date);
        return !!noteDate && noteDate >= filterStart && noteDate <= filterEnd;
      })
      .map(note => ({
        ...note,
        taskId: task.id,
        taskTitle: task.title,
        noteDate: parseDateLike((note as any).createdAt || (note as any).date) || new Date(0),
        noteContent: (note as any).content || (note as any).note,
      }))
  ).sort((a, b) => b.noteDate.getTime() - a.noteDate.getTime());
  
  // Calculate statistics
  const stats = {
    totalTasks: allTasks.length,
    completed: allTasks.filter(t => t.status === 'Completed' || String(t.status) === 'Done').length,
    inProgress: allTasks.filter(t => t.status === 'In Progress').length,
    blocked: allTasks.filter(t => t.status === 'Blocked').length,
    estimatedHours: allTasks.reduce((sum, t) => sum + (t.estimatedHours || 0), 0),
    loggedHours: allTasks.reduce((sum, t) => {
      const logged = t.timeLogs?.reduce((s, log) => s + log.hours, 0) || 0;
      return sum + logged;
    }, 0),
    estimatedCost: financeTasks.reduce((sum, t) => sum + ((t.estimatedHours || 0) * defaultCostPerHour), 0),
    actualCost: financeTasks.reduce((sum, t) => {
      const logged = t.timeLogs?.reduce((s, log) => s + log.hours, 0) || 0;
      return sum + (logged * defaultCostPerHour);
    }, 0),
  };
  
  // Tasks over budget
  const tasksOverBudget = allTasks.filter(task => {
    const logged = task.timeLogs?.reduce((sum, log) => sum + log.hours, 0) || 0;
    return logged > (task.estimatedHours || 0);
  });
  
  // Calculate expected completion date based on current velocity
  const completionPercentage = stats.estimatedHours > 0 ? (stats.loggedHours / stats.estimatedHours) * 100 : 0;
  const daysElapsed = 30; // Placeholder - would calculate from project start date
  const estimatedTotalDays = completionPercentage > 0 ? (daysElapsed / completionPercentage) * 100 : 0;
  const estimatedCompletionDate = new Date(Date.now() + (estimatedTotalDays - daysElapsed) * 24 * 60 * 60 * 1000);

  return (
    <div className="min-h-screen bg-muted/40">
      <div className="border-b bg-white">
        <div className="container mx-auto px-4 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Stakeholder Dashboard</h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                {state.currentOrganization?.name} • {primaryProject?.name || 'All Projects'}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => navigate('/dashboard')} className="text-sm">
                My Tasks
              </Button>
              <Button variant="outline" onClick={() => navigate('/app')} className="text-sm">
                Full System
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 sm:py-8">
        {/* Milestone Tracker */}
        <div className="mb-6 sm:mb-8">
          <MilestoneTracker
            tasks={allTasks}
            users={orgUsers}
            canChangeMilestone={true}
            activeMilestone={
              (localStorage.getItem('activeMilestone') as Priority) || 'P0'
            }
            onMilestoneChange={(milestone) => {
              localStorage.setItem('activeMilestone', milestone);
              window.location.reload(); // Reload to reflect changes
            }}
          />
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs sm:text-sm text-muted-foreground">Completion</div>
                  <div className="text-xl sm:text-2xl font-bold">{((stats.completed / stats.totalTasks) * 100).toFixed(0)}%</div>
                  <div className="text-xs text-muted-foreground">{stats.completed}/{stats.totalTasks} tasks</div>
                </div>
                <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-chart-2" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs sm:text-sm text-muted-foreground">Hours Progress</div>
                  <div className="text-xl sm:text-2xl font-bold">{completionPercentage.toFixed(0)}%</div>
                  <div className="text-xs text-muted-foreground">{stats.loggedHours.toFixed(0)}/{stats.estimatedHours.toFixed(0)}h</div>
                </div>
                <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs sm:text-sm text-muted-foreground">Estimated Cost</div>
                  {canViewAnyFinance ? (
                    <>
                      <div className="text-xl sm:text-2xl font-bold">${(stats.estimatedCost / 1000).toFixed(0)}k</div>
                      <div className="text-xs text-muted-foreground">@${defaultCostPerHour}/hr</div>
                    </>
                  ) : (
                    <>
                      <div className="text-xl sm:text-2xl font-bold text-muted-foreground">Restricted</div>
                      <div className="text-xs text-muted-foreground">Finance access required</div>
                    </>
                  )}
                </div>
                <DollarSign className="h-6 w-6 sm:h-8 sm:w-8 text-chart-4" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs sm:text-sm text-muted-foreground">Actual Cost</div>
                  {canViewAnyFinance ? (
                    <>
                      <div className={`text-xl sm:text-2xl font-bold ${stats.actualCost > stats.estimatedCost ? 'text-destructive' : 'text-chart-2'}`}>
                        ${(stats.actualCost / 1000).toFixed(0)}k
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {stats.actualCost > stats.estimatedCost ? '+' : ''}{((stats.actualCost - stats.estimatedCost) / 1000).toFixed(0)}k
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-xl sm:text-2xl font-bold text-muted-foreground">Restricted</div>
                      <div className="text-xs text-muted-foreground">Finance access required</div>
                    </>
                  )}
                </div>
                <DollarSign className={`h-6 w-6 sm:h-8 sm:w-8 ${stats.actualCost > stats.estimatedCost ? 'text-destructive' : 'text-chart-2'}`} />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Projected Completion */}
            <Card>
              <CardHeader>
                <CardTitle>Projected Completion</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-muted-foreground">Estimated Completion Date</div>
                      <div className="text-2xl font-bold">
                        {estimatedCompletionDate.toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </div>
                    </div>
                    <Calendar className="h-8 w-8 text-primary" />
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Based on current velocity: {completionPercentage.toFixed(1)}% complete
                  </div>
                  <Progress value={Math.min(completionPercentage, 100)} className="h-3" />
                </div>
              </CardContent>
            </Card>

            {/* Tasks Over Budget */}
            {tasksOverBudget.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-destructive" />
                    Tasks Over Budget ({tasksOverBudget.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {tasksOverBudget.slice(0, 5).map(task => {
                      const logged = task.timeLogs?.reduce((sum, log) => sum + log.hours, 0) || 0;
                      const estimated = task.estimatedHours || 0;
                      const overBy = logged - estimated;
                      return (
                        <div 
                          key={task.id} 
                          className="flex items-center justify-between border-b pb-2 last:border-0"
                        >
                          <div className="flex-1">
                            <div className="font-medium">{task.title}</div>
                            <div className="text-sm text-muted-foreground">
                              {logged.toFixed(1)}h / {estimated}h
                            </div>
                          </div>
                          <Badge variant="destructive">
                            +{overBy.toFixed(1)}h
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Standup Notes */}
            <Card>
              <CardHeader>
                <CardTitle>Standup Notes</CardTitle>
                <CardDescription>
                  Showing {standupNotesInRange.length} updates
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Date Range Filter */}
                <div className="mb-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Time Period</Label>
                      <Select value={dateRange} onValueChange={(v: any) => setDateRange(v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="today">Today</SelectItem>
                          <SelectItem value="week">Last 7 Days</SelectItem>
                          <SelectItem value="month">Last 30 Days</SelectItem>
                          <SelectItem value="custom">Custom Range</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {dateRange === 'custom' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Start Date</Label>
                        <Input 
                          type="date" 
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>End Date</Label>
                        <Input 
                          type="date" 
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Notes List */}
                <div className="space-y-4">
                  {standupNotesInRange.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No standup notes in this time period
                    </div>
                  ) : (
                    standupNotesInRange.map((note) => (
                      <div key={note.id} className="border-l-2 border-primary/40 pl-4 py-2">
                        <div className="flex items-start justify-between mb-1">
                          <div className="font-medium text-sm">
                            <Badge variant="outline" className="mr-2">{note.taskId}</Badge>
                            {note.taskTitle}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {note.noteDate.toLocaleDateString('en-US', { 
                              weekday: 'short',
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">{note.noteContent}</p>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 sm:space-y-6">
            {/* Cost Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Cost Analysis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {canViewAnyFinance ? (
                  <>
                    <div>
                      <div className="text-sm text-muted-foreground">Cost per Hour</div>
                      <div className="text-2xl font-bold">${defaultCostPerHour}</div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Estimated</span>
                        <span className="font-medium">${stats.estimatedCost.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Actual</span>
                        <span className={`font-medium ${stats.actualCost > stats.estimatedCost ? 'text-destructive' : 'text-chart-2'}`}>
                          ${stats.actualCost.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between pt-2 border-t">
                        <span className="text-sm font-medium">Variance</span>
                        <span className={`font-bold ${stats.actualCost > stats.estimatedCost ? 'text-destructive' : 'text-chart-2'}`}>
                          {stats.actualCost > stats.estimatedCost ? '+' : '-'}
                          ${Math.abs(stats.actualCost - stats.estimatedCost).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Cost analysis is restricted to project admins or owners.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Status Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Task Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Completed</span>
                  <Badge className="bg-chart-2/10 text-chart-2">{stats.completed}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">In Progress</span>
                  <Badge className="bg-primary/10 text-primary">{stats.inProgress}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Blocked</span>
                  <Badge className="bg-destructive/10 text-destructive">{stats.blocked}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Not Started</span>
                  <Badge className="bg-muted/40 text-muted-foreground">
                    {stats.totalTasks - stats.completed - stats.inProgress - stats.blocked}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Project Info */}
            {primaryProject && (
              <Card>
                <CardHeader>
                  <CardTitle>Project Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div>
                    <div className="text-muted-foreground">Project Name</div>
                    <div className="font-medium mt-1">{primaryProject.name}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Status</div>
                    <Badge className="mt-1">{primaryProject.status}</Badge>
                  </div>
                  {canViewProjectFinance(state.currentUser, primaryProject) && primaryProject.costs && primaryProject.costs.length > 0 && (
                    <div>
                      <div className="text-muted-foreground mb-2">Budget Breakdown</div>
                      {primaryProject.costs.map((cost: any) => (
                        <div key={cost.id} className="flex justify-between text-xs mb-1">
                          <span>{ensureCostItemCode(cost)} - {cost.category}</span>
                          <span className="font-medium">${(cost.amount / 1000).toFixed(0)}k</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}